/**
 * CR071: Hook for managing merchant data and logos
 */

import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface Merchant {
  id: string;
  merchant_key: string;
  display_name: string;
  logo_url: string | null;
  logo_svg: string | null;
  logo_source: string | null;
  logo_attribution: string | null;
  logo_updated_at: string | null;
  category: string | null;
  created_at: string;
  updated_at: string;
}

export function useMerchants() {
  const [merchants, setMerchants] = useState<Merchant[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchMerchants = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("merchants")
        .select("*")
        .order("display_name");

      if (error) throw error;
      setMerchants((data as Merchant[]) || []);
    } catch (err) {
      console.error("Error fetching merchants:", err);
      toast({
        title: "Error",
        description: "Failed to fetch merchants",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchMerchants();
  }, [fetchMerchants]);

  const getMerchantByKey = useCallback(
    (merchantKey: string): Merchant | null => {
      return merchants.find((m) => m.merchant_key === merchantKey) || null;
    },
    [merchants]
  );

  const updateMerchantLogo = useCallback(
    async (
      merchantKey: string,
      logoData: { logo_url?: string | null; logo_svg?: string | null; logo_source?: string }
    ) => {
      try {
        const { error } = await supabase
          .from("merchants")
          .update({
            ...logoData,
            logo_updated_at: new Date().toISOString(),
          })
          .eq("merchant_key", merchantKey);

        if (error) throw error;

        // Refresh merchants
        await fetchMerchants();
        
        toast({
          title: "Logo updated",
          description: `Logo for ${merchantKey} has been updated`,
        });

        return true;
      } catch (err) {
        console.error("Error updating merchant logo:", err);
        toast({
          title: "Error",
          description: "Failed to update merchant logo",
          variant: "destructive",
        });
        return false;
      }
    },
    [fetchMerchants, toast]
  );

  const uploadMerchantLogo = useCallback(
    async (merchantKey: string, file: File) => {
      try {
        const ext = file.name.split(".").pop()?.toLowerCase() || "png";
        const filePath = `${merchantKey}.${ext}`;

        // Upload to storage
        const { error: uploadError } = await supabase.storage
          .from("merchant-logos")
          .upload(filePath, file, { upsert: true });

        if (uploadError) throw uploadError;

        // Get public URL
        const { data: { publicUrl } } = supabase.storage
          .from("merchant-logos")
          .getPublicUrl(filePath);

        // Update merchant record
        return await updateMerchantLogo(merchantKey, {
          logo_url: publicUrl,
          logo_svg: null,
          logo_source: "uploaded",
        });
      } catch (err) {
        console.error("Error uploading logo:", err);
        toast({
          title: "Error",
          description: "Failed to upload logo",
          variant: "destructive",
        });
        return false;
      }
    },
    [updateMerchantLogo, toast]
  );

  const createMerchant = useCallback(
    async (merchantKey: string, displayName: string, categoryId: string) => {
      try {
        const { error } = await supabase.from("merchants").insert({
          merchant_key: merchantKey,
          display_name: displayName,
          logo_source: "legacy",
          category_id: categoryId, // CR403: Use category_id FK, not text category
          category: "Legacy", // Legacy text field, kept for compatibility
        });

        if (error) throw error;
        await fetchMerchants();
        
        toast({
          title: "Merchant created",
          description: `${displayName} has been added to the directory`,
        });

        return true;
      } catch (err) {
        console.error("Error creating merchant:", err);
        toast({
          title: "Error",
          description: "Failed to create merchant",
          variant: "destructive",
        });
        return false;
      }
    },
    [fetchMerchants, toast]
  );

  return {
    merchants,
    loading,
    fetchMerchants,
    getMerchantByKey,
    updateMerchantLogo,
    uploadMerchantLogo,
    createMerchant,
  };
}
